import { z } from "zod"

/**
 * In-app purchase endpoints (T1160, docs/design/iap-architecture.md section 4). Both sit behind
 * the server's `STORE_BILLING_ENABLED` switch and answer 404 while it is off; clients only call
 * them when `GET /api/v1/me` returned `storePurchasing: true`. Only the organisation Owner may
 * call them, and only for a personal organisation (Teams are bought on the web).
 *
 * Requests are strict. Responses are lenient (old clients ignore added fields).
 */
export const storeNameSchema = z.enum(["apple", "google"])

/** POST /api/v1/billing/store/account: get the stable account token to embed in a purchase. */
export const storeAccountRequestSchema = z.strictObject({
  store: storeNameSchema,
  /** Defaults to the caller's personal organisation. */
  organisationId: z.uuid().optional(),
})

/** The token goes into StoreKit `appAccountToken` (a UUID) or Play `obfuscatedAccountId`. */
export const storeAccountResponseSchema = z.object({
  // Lenient: a store added later must not break an old client's parse (response schemas stay open).
  store: z.string(),
  accountToken: z.string(),
})

/**
 * POST /api/v1/billing/store/attach: hand a finished or restored purchase to the server, which
 * verifies it with the store (never trusting the client) and links it to the organisation.
 * Apple: `signedTransaction` is the StoreKit 2 `jwsRepresentation`. Google: `purchaseToken` plus
 * the app's `packageName`.
 */
export const storeAttachRequestSchema = z.discriminatedUnion("store", [
  z.strictObject({
    store: z.literal("apple"),
    organisationId: z.uuid().optional(),
    signedTransaction: z.string().min(1).max(20_000),
  }),
  z.strictObject({
    store: z.literal("google"),
    organisationId: z.uuid().optional(),
    purchaseToken: z.string().min(1).max(4_000),
    packageName: z.string().min(1).max(200),
  }),
])

export const storeAttachResponseSchema = z.object({
  provider: z.string(),
  plan: z.string(),
  status: z.string(),
  currentPeriodEnd: z.string().nullable().optional(),
})

export type StoreAccountRequest = z.infer<typeof storeAccountRequestSchema>
export type StoreAccountResponse = z.infer<typeof storeAccountResponseSchema>
export type StoreAttachRequest = z.infer<typeof storeAttachRequestSchema>
export type StoreAttachResponse = z.infer<typeof storeAttachResponseSchema>
